import React from 'react';
import { cn } from '../../lib/utils';
import { CalendarIcon, CalendarDaysIcon } from 'lucide-react';
interface MetricsDateFilterProps {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
}
type PresetKey = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'last3Months';
interface Preset {
  key: PresetKey;
  label: string;
}
const PRESETS: Preset[] = [{
  key: 'today',
  label: 'Hoy'
}, {
  key: 'yesterday',
  label: 'Ayer'
}, {
  key: 'thisWeek',
  label: 'Esta Semana'
}, {
  key: 'thisMonth',
  label: 'Este Mes'
}, {
  key: 'lastMonth',
  label: 'Último Mes'
}, {
  key: 'last3Months',
  label: '3 Meses'
}];
function getPresetDates(key: PresetKey): {
  start: string;
  end: string;
} {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  switch (key) {
    case 'today':
      return {
        start: fmt(today),
        end: fmt(today)
      };
    case 'yesterday':
      {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          start: fmt(yesterday),
          end: fmt(yesterday)
        };
      }
    case 'thisWeek':
      {
        const dayOfWeek = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        return {
          start: fmt(monday),
          end: fmt(today)
        };
      }
    case 'thisMonth':
      {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          start: fmt(firstDay),
          end: fmt(today)
        };
      }
    case 'lastMonth':
      {
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          start: fmt(firstDay),
          end: fmt(lastDay)
        };
      }
    case 'last3Months':
      {
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        return {
          start: fmt(firstDay),
          end: fmt(today)
        };
      }
  }
}
function getActivePreset(startDate: string, endDate: string): PresetKey | null {
  for (const preset of PRESETS) {
    const {
      start,
      end
    } = getPresetDates(preset.key);
    if (start === startDate && end === endDate) return preset.key;
  }
  return null;
}
export function MetricsDateFilter({
  startDate,
  endDate,
  onChange
}: MetricsDateFilterProps) {
  const activePreset = getActivePreset(startDate, endDate);
  return <div className="space-y-3" data-id="element-596">
      {/* Preset Buttons */}
      <div className="flex flex-wrap gap-2" data-id="element-597">
        {PRESETS.map(preset => <button key={preset.key} onClick={() => {
        const {
          start,
          end
        } = getPresetDates(preset.key);
        onChange(start, end);
      }} className={cn('px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-150', activePreset === preset.key ? 'bg-gray-900 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:text-gray-900')} data-id="element-598">
            {preset.label}
          </button>)}
      </div>

      {/* Custom Date Inputs */}
      <div className="flex items-center gap-3 flex-wrap" data-id="element-599">
        <div className="flex items-center gap-2" data-id="element-600">
          <CalendarIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-601" />
          <label className="text-sm text-gray-500 font-medium" data-id="element-602">Desde</label>
          <input type="date" value={startDate} onChange={e => onChange(e.target.value, endDate)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300" data-id="element-603" />
        </div>
        <div className="flex items-center gap-2" data-id="element-604">
          <CalendarDaysIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-605" />
          <label className="text-sm text-gray-500 font-medium" data-id="element-606">Hasta</label>
          <input type="date" value={endDate} onChange={e => onChange(startDate, e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300" data-id="element-607" />
        </div>
      </div>
    </div>;
}